/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useState } from 'react';
import { Stepper, Step, StepLabel, Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';

export type StepData = {
  label: string;
  optionalText?: string;
};

type StatusDisplayProps = {
  steps: StepData[];
  currentStep: number;
};

export const StatusDisplay: React.FC<StatusDisplayProps> = ({
  steps,
  currentStep,
}) => {
  const [ellipsis, setEllipsis] = useState<string>('');
  const articleState = useSelector((state: RootState) => state.article);

  useEffect(() => {
    const interval = setInterval(() => {
      setEllipsis((prev) => (prev.length < 3 ? prev + '.' : ''));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={currentStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={index}>
            <StepLabel
              optional={
                step.optionalText ? (
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {step.optionalText}
                  </Typography>
                ) : null
              }
            >
              {index === currentStep && articleState.isGenerating ? (
                <Typography>
                  {step.label}
                  <span>{ellipsis}</span>
                </Typography>
              ) : (
                step.label
              )}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};
